// ActionGrid — desktop-only 3-tile row (Tạo bài AI / Chia sẻ Zalo /
// Liên kết) shown in the right-column details stack. Mobile reaches
// the same actions through `MobileMoreSheet`, so the three tiles do
// not need a `variant` switch here.
//
// The tile components live in the same file: they're never reused
// apart, and inlining them keeps the desktop action cluster as one
// readable unit. Only `ActionGrid` is exported.

import { Share, Sparkles } from "lucide-react";

import { useZaloShareHandler } from "@/features/property/hooks/use-zalo-share-handler";
import { usePublicShareHandler } from "@/features/property/hooks/use-public-share-handler";
import type { Property } from "@/types/property";

interface ActionGridProps {
  property: Property;
  onOpenAi: () => void;
}

export function ActionGrid({ property, onOpenAi }: ActionGridProps) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      <AiPostTile onClick={onOpenAi} />
      <ZaloShareTile property={property} />
      <PublicLinkTile property={property} />
    </div>
  );
}

function AiPostTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1 rounded-[10px] border border-primary/25 bg-gradient-to-br from-accent to-background px-2.5 py-2.5 text-center"
    >
      <Sparkles aria-hidden="true" className="size-[15px] text-primary" />
      <span className="text-[11px] font-bold text-primary">Tạo bài AI</span>
    </button>
  );
}

function ZaloShareTile({ property }: { property: Property }) {
  // Calls the same `useZaloShareHandler` that the mobile More sheet uses.
  // Visual treatment is owned here; the share semantics are owned by the
  // hook so the two surfaces stay in lock-step.
  const { busy, trigger } = useZaloShareHandler(property);
  return (
    <button
      type="button"
      onClick={() => void trigger()}
      disabled={busy}
      className="flex flex-col items-center gap-1 rounded-[10px] border border-border bg-background px-2.5 py-2.5 text-center disabled:opacity-60"
    >
      <span
        aria-hidden
        className="flex size-[18px] items-center justify-center rounded-[4px] bg-zalo font-mono text-[11px] font-bold text-white"
      >
        Z
      </span>
      <span className="text-[11px] font-semibold">Chia sẻ Zalo</span>
    </button>
  );
}

function PublicLinkTile({ property }: { property: Property }) {
  // See ZaloShareTile — share semantics live in usePublicShareHandler so
  // mobile More sheet and desktop action grid call into one implementation.
  const { busy, trigger } = usePublicShareHandler(property);
  return (
    <button
      type="button"
      onClick={() => void trigger()}
      disabled={busy}
      className="flex flex-col items-center gap-1 rounded-[10px] border border-border bg-background px-2.5 py-2.5 text-center disabled:opacity-60"
    >
      <Share aria-hidden="true" className="size-[14px]" />
      <span className="text-[11px] font-semibold">Liên kết</span>
    </button>
  );
}
